import type { ExerciseType } from "@calega/content";
import { hypothesisExplanation } from "./hypothesis-explanations";

export type QualitativeCoverage = "complete" | "partial" | "none";
export type QualitativeSubject = "math" | "german";
export type QualitativeMacroPolarity = "concern" | "strength";

export interface QualitativeMacroDomain {
  id: string;
  label: string;
  subject: QualitativeSubject;
  explanation: string;
}

export interface QualitativeMacroCluster {
  id: string;
  label: string;
  subject: QualitativeSubject;
  domainId: string | null;
  polarity: QualitativeMacroPolarity;
  explanation: string;
  ruleIds: readonly string[];
  evaluationUnits: readonly string[];
  additiveAcrossLogs: boolean;
}

export interface QualitativeAnalysisVariant {
  label: string;
  coverage: QualitativeCoverage;
  explanation: string;
}

export interface QualitativeAnalysisCatalogEntry {
  exerciseType: ExerciseType;
  label: string;
  subject: QualitativeSubject;
  coverage: QualitativeCoverage;
  microAnalysis: boolean;
  macroAnalysis: boolean;
  analysisForm: "rule_analyzer" | "word_diagnostics" | "none";
  theory: string;
  diagnosisUnit: string;
  denominator: string;
  limitations: string;
  variants: QualitativeAnalysisVariant[];
  hypotheses: Array<ReturnType<typeof hypothesisExplanation> & {
    ruleId: string;
    macroAggregation: boolean;
    macroClusterId: string;
    macroDomainId: string | null;
    macroPolarity: QualitativeMacroPolarity;
  }>;
}

export const qualitativeMacroThresholds = Object.freeze({
  relevantMinimum: 5,
  suspectedRate: 0.3,
  manifestRate: 0.6,
  manifestEvidenceMinimum: 3,
  explanation: "Makro-Status werden nur aus fachlich relevanten Einheiten gebildet. Unter fünf relevanten Einheiten lautet der Status „Zu wenig Daten“. Bei ausreichender Datenmenge und unter 30 % gilt ein Muster als unauffällig. Ab 30 % gilt es als Verdacht. Ab 60 % und mindestens drei Belegen gilt es als auffällig beziehungsweise manifest.",
  strengthExplanation: "Positive Strukturhinweise werden mit denselben Mindestmengen, aber einer eigenen Stärke-Skala bewertet: unter fünf relevanten Einheiten „Zu wenig Daten“, darunter „Nicht beobachtet“, ab 30 % „Im Aufbau“ und ab 60 % bei mindestens drei Belegen „Gefestigt“.",
});

export const qualitativeMacroDomains: readonly QualitativeMacroDomain[] = Object.freeze([
  { id: "math.number_sequence", label: "Zahlfolge und Nachbarzahlen", subject: "math", explanation: "Hinweise auf Abweichungen zu direkt benachbarten Zahlen oder Ergebnissen." },
  { id: "math.operations", label: "Operationsverständnis", subject: "math", explanation: "Hinweise darauf, wie sicher Rechenzeichen und Umkehroperationen unterschieden werden." },
  { id: "math.place_value", label: "Stellenwertverständnis", subject: "math", explanation: "Hinweise zur Bedeutung und Reihenfolge von Ziffern sowie zu Zehner-Einer-Strukturen." },
  { id: "math.facts_rules", label: "Rechenfakten und Strukturregeln", subject: "math", explanation: "Hinweise zu automatisierten Faktenfamilien und grundlegenden Rechenregeln." },
  { id: "math.part_whole", label: "Teil-Ganzes und Weiterrechnen", subject: "math", explanation: "Hinweise zu mehrschrittigen Zusammenhängen und zum rechnerisch stimmigen Fortsetzen eigener Zwischenwerte." },
  { id: "math.quantity_number", label: "Menge-Zahl-Zuordnung", subject: "math", explanation: "Hinweise zur Zuordnung dargestellter Anzahlen zu Zahlwerten." },
  { id: "math.rounding", label: "Rundungsverständnis", subject: "math", explanation: "Hinweise zu Zielstelle, Richtung und Veränderung beim Runden." },
  { id: "math.written_procedures", label: "Schriftliche Rechenverfahren", subject: "math", explanation: "Hinweise zu Überträgen, Entbündelungen und ihrer korrekten Verwendung." },
]);

type EntryDefinition = Omit<QualitativeAnalysisCatalogEntry, "exerciseType" | "hypotheses"> & { ruleIds: readonly string[] };

const entries = {
  "laute-erkennen": { label: "Laute erkennen", subject: "german", coverage: "complete", microAnalysis: true, macroAnalysis: true, analysisForm: "word_diagnostics", theory: "Freie mündliche Antworten werden durch die leitende Fachkraft bewertet. Vereinfachte Lautgruppen sind keine Schreibweisen.", diagnosisUnit: "Wort, abgefragte Position und erwartete Lautgruppe; pro Durchlauf semantisch einmal.", denominator: "Eindeutige Bewertungen; Verwechslungen nur mit erfasster Lautgruppe. Unsicherheit und Auslassung werden separat gezählt.", limitations: "Keine Spracherkennung oder Diagnose. Keine Trennung der beiden ch-Varianten oder der Vokallängen. Eigene Wörter besitzen manuell bestätigte Lautangaben.", variants: [], ruleIds: ["laute-erkennen.initial", "laute-erkennen.final", "laute-erkennen.substitution", "laute-erkennen.opposite"] },
  quiz: {
    label: "Quiz",
    subject: "german",
    coverage: "none",
    microAnalysis: false,
    macroAnalysis: false,
    analysisForm: "none",
    theory: "Ein Quiz bewertet derzeit nur, ob die gewählte Antwortkombination vollständig richtig ist. Distraktoren werden noch nicht als fachlich typisierte Fehlentscheidungen ausgewertet.",
    diagnosisUnit: "Noch nicht festgelegt; möglich wären Frage, Antwortoption oder fachlich typisierter Distraktor.",
    denominator: "Es gibt noch keinen qualitativen Nenner.",
    limitations: "Für eine qualitative Analyse müssten Distraktoren fachlich klassifiziert und pro Quizbereich eigene Hypothesen beschlossen werden.",
    variants: [], ruleIds: [],
  },
  lueckentext: {
    label: "Lückentext",
    subject: "german",
    coverage: "none",
    microAnalysis: false,
    macroAnalysis: false,
    analysisForm: "none",
    theory: "Lückentexte speichern fachliche Antworten, unterscheiden aber noch nicht zwischen orthographischen, grammatischen und inhaltlichen Fehlertypen.",
    diagnosisUnit: "Noch nicht festgelegt; je nach Inhalt kämen Lücke, Wort oder grammatische Zielstruktur infrage.",
    denominator: "Es gibt noch keinen qualitativen Nenner.",
    limitations: "Der Aufgabentyp ist inhaltlich zu offen für eine gemeinsame Hypothesenlogik. Zuerst wäre ein deklaratives Lernziel pro Lücke nötig.",
    variants: [], ruleIds: [],
  },
  rechenmauer: {
    label: "Rechenmauer",
    subject: "math",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Die Analyse trennt lokale Ausgangsfehler von logisch konsistenten Folgefehlern und betrachtet zusätzlich typische Nachbar- und Inversionsfehler.",
    diagnosisUnit: "Ein auszufüllendes Feld der Rechenmauer und seine Abhängigkeit von den beiden Feldern darunter.",
    denominator: "Je Hypothese zählen nur auswertbare Eingabefelder beziehungsweise echte Folgefehler-Gelegenheiten.",
    limitations: "Die Hinweise beschreiben rechnerische Muster. Sie belegen allein weder eine Rechenstörung noch eine bestimmte Bearbeitungsstrategie.",
    variants: [],
    ruleIds: ["rechenmauer.plus_minus_one", "rechenmauer.consistent_followup", "rechenmauer.inversion"],
  },
  mathe: {
    label: "Rechentraining",
    subject: "math",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Die Analyse vergleicht falsche Ergebnisse mit fachlich typischen Alternativrechnungen, Nachbarergebnissen und Strukturregeln der vier Grundrechenarten.",
    diagnosisUnit: "Eine einzelne Rechenaufgabe mit Operation, Operanden, Sollwert und eingegebenem Ergebnis.",
    denominator: "Für jede Hypothese zählen nur Aufgaben, bei denen das jeweilige Muster mathematisch eindeutig prüfbar ist.",
    limitations: "Ein passendes Fehlerergebnis ist ein regelbasierter Hinweis. Es beweist nicht, welcher mentale Lösungsweg tatsächlich verwendet wurde.",
    variants: [],
    ruleIds: ["mathe.counting_error_1", "mathe.counting_error_2", "mathe.two_digit_inversion", "mathe.plus_minus_confusion", "mathe.mult_div_confusion", "mathe.multiplication_table_slip", "mathe.addition_instead_of_multiplication", "mathe.concatenation", "mathe.multiplication_zero_rule", "mathe.multiplication_one_rule", "mathe.division_quotient_slip", "mathe.multiplication_instead_of_division", "mathe.subtraction_instead_of_division", "mathe.division_blind_halving", "mathe.divisor_perseveration", "mathe.division_identity_rule", "mathe.division_self_rule", "mathe.place_value_fusion", "mathe.place_value_subtraction_inversion"],
  },
  einmaleins: {
    label: "1×1-Trainer",
    subject: "math",
    coverage: "partial",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Der Trainer übt ausgewählte Multiplikationsreihen oder gemischte Kernaufgaben aus der 1er-, 2er-, 5er- und 10er-Reihe sowie Quadrataufgaben bis 10×10 und speichert fachliche Antworten im normalen Lernverlauf.",
    diagnosisUnit: "Eine vollständige Multiplikationsaufgabe mit beiden Operanden, Sollwert und eingegebenem Ergebnis. Tauschaufgaben bleiben getrennte Aufgaben.",
    denominator: "Pro Regel zählen nur Multiplikationsaufgaben, bei denen das fachliche Fehlermuster prüfbar ist. Auslassungen werden nicht als Strategiefehler interpretiert.",
    limitations: "Die Analyse beschreibt fachliche Fehlermuster, keinen Nachweis automatisierten Abrufs. Frühere V2-Läufe behalten ihren Aufgabenverlauf ohne nachträglich erzeugte Hypothesen. Importierte V1-Analysen bleiben erhalten. Itembezogene Zeit-, Fokus- und Latenzdaten werden nicht erhoben.",
    variants: [
      { label: "Reihen üben (2–10)", coverage: "partial", explanation: "Ausgewählte Reihen werden mit den bestehenden fachlichen Multiplikationsregeln ausgewertet." },
      { label: "Kernaufgaben", coverage: "partial", explanation: "Die 1er-, 2er-, 5er- und 10er-Reihe sowie Quadrataufgaben nutzen dieselben Multiplikationsregeln und Schwellen; es gibt keine zusätzliche Gruppendiagnostik." },
    ], ruleIds: ["mathe.two_digit_inversion", "mathe.mult_div_confusion", "mathe.multiplication_table_slip", "mathe.addition_instead_of_multiplication", "mathe.concatenation", "mathe.multiplication_one_rule"],
  },
  "stellenwert-addition": {
    label: "Zehner zuerst addieren",
    subject: "math",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Die Analyse prüft die Übertragung der Sachsituation in Zahlen sowie die Strategiefolge „erst Zehner, dann Einer“ und trennt Ausgangs- von Folgefehlern.",
    diagnosisUnit: "Ein vollständiges Item mit Ausgangsmengen, Übertragung, Zwischenergebnis und Endergebnis.",
    denominator: "Je Regel zählen nur Items, in denen Zehner- und Eineranteile eindeutig unterscheidbar und die betreffende Strategieentscheidung prüfbar ist.",
    limitations: "Stimmiges Weiterrechnen wird als positiver Strukturhinweis mit eigener Stärke-Skala dokumentiert und nicht als Defizit bewertet.",
    variants: [],
    ruleIds: ["stellenwert_addition.tens_as_ones", "stellenwert_addition.ones_first", "stellenwert_addition.tens_omitted", "stellenwert_addition.ones_omitted", "stellenwert_addition.operand_order_swapped", "stellenwert_addition.consistent_followup"],
  },
  transkodieren: {
    label: "Zahlwörter in Zahlen umwandeln", subject: "math", coverage: "complete", microAnalysis: true, macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Beschreibt Einer-Zehner-Vertauschungen und ausgelassene oder zusätzliche Nullen im ersten Versuch. Auswahl und freie Eingabe werden getrennt ausgewertet.",
    diagnosisUnit: "Ein Zahlwort, seine Zielzahl und die erste abgegebene Antwort.",
    denominator: "Nur fachlich geeignete Items zählen: unterschiedliche Einer und Zehner für Inversionen, vorhandene Nullen für ausgelassene Nullen. Zweite Versuche beeinflussen die Analyse nicht.",
    limitations: "Einzelne Fehler sind Beobachtungen, keine Diagnose ihrer Ursache. Bei zu wenigen geeigneten Aufgaben wird kein übergreifendes Muster abgeleitet.",
    variants: [],
    ruleIds: ["transkodieren.choice.inversion", "transkodieren.choice.zero_omitted", "transkodieren.choice.zero_added", "transkodieren.input.inversion", "transkodieren.input.zero_omitted", "transkodieren.input.zero_added"],
  },
  runden: {
    label: "Runden",
    subject: "math",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Die Analyse unterscheidet eine falsche Rundungsrichtung, eine falsche Zielstelle und das unveränderte Übernehmen der Ausgangszahl.",
    diagnosisUnit: "Eine Zahl, die geforderte Rundungsstelle und das eingegebene Ergebnis.",
    denominator: "Es zählen nur Items, bei denen das alternative Ergebnis einer Hypothese eindeutig vom korrekten Ergebnis unterscheidbar ist.",
    limitations: "Der Generator vermeidet bereits passend gerundete Ausgangszahlen, damit „unverändert“ eindeutig auswertbar bleibt.",
    variants: [],
    ruleIds: ["runden.direction_error", "runden.target_place_error", "runden.unchanged"],
  },
  "zahlen-zerlegen": {
    label: "Zahlen zerlegen",
    subject: "math",
    coverage: "none",
    microAnalysis: false,
    macroAnalysis: false,
    analysisForm: "none",
    theory: "Derzeit wird nur geprüft, ob die ausgewählten Zahlen die Zielsumme bilden. Verschiedene gültige Zerlegungen werden bewusst gleichwertig akzeptiert.",
    diagnosisUnit: "Noch nicht festgelegt; möglich wären Auswahlmenge, Teil-Ganzes-Beziehung oder gewählte Zerlegungsstrategie.",
    denominator: "Es gibt noch keinen qualitativen Nenner.",
    limitations: "Vor einer Analyse muss geklärt werden, welche von mehreren gültigen Zerlegungen diagnostisch unterscheidbar sein sollen.",
    variants: [], ruleIds: [],
  },
  "frosch-hupfen": {
    label: "Frosch-Hüpfen",
    subject: "math",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Die Analyse überträgt die fachlich identischen Additions- und Subtraktionsmuster auf den Zahlenstrahl: Nachbarfehler, vertauschte Rechenrichtung und Zehner-Einer-Fehler beim Rückwärtssprung.",
    diagnosisUnit: "Ein Sprung mit Startpunkt, Schrittweite, Richtung, Sollziel und gewähltem Ziel.",
    denominator: "Je Hypothese zählen nur Sprünge, bei denen das typische Fehlziel im sichtbaren Zahlenraum auswählbar und vom richtigen Ziel unterscheidbar ist.",
    limitations: "Ein passendes Fehlziel ist ein regelbasierter Hinweis und belegt nicht, ob tatsächlich gezählt, das Zeichen verwechselt oder eine andere Strategie genutzt wurde.",
    variants: [],
    ruleIds: ["frosch-hupfen.counting_error_1", "frosch-hupfen.counting_error_2", "frosch-hupfen.plus_minus_confusion", "frosch-hupfen.place_value_subtraction_inversion"],
  },
  waage: {
    label: "Waage – Mengen vergleichen",
    subject: "math",
    coverage: "none",
    microAnalysis: false,
    macroAnalysis: false,
    analysisForm: "none",
    theory: "Die Übung bewertet links, gleich oder rechts. Mengendifferenz, Gleichheitsverständnis und visuelle Anordnung werden noch nicht qualitativ getrennt.",
    diagnosisUnit: "Noch nicht festgelegt; möglich wären Mengenpaar und gewählte Vergleichsrelation.",
    denominator: "Es gibt noch keinen qualitativen Nenner.",
    limitations: "Eine Analyse müsste Gleichheitsitems und Ungleichheitsitems getrennt behandeln.",
    variants: [], ruleIds: [],
  },
  "mengen-match": {
    label: "Mengen-Match",
    subject: "math",
    coverage: "partial",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Die Analyse prüft ausschließlich, ob die gewählte Zahl um eins oder zwei von der dargestellten Anzahl abweicht.",
    diagnosisUnit: "Eine dargestellte Menge mit Sollzahl und gewählter Zahl.",
    denominator: "Je Abstand zählen nur Items, bei denen eine entsprechende Zahl als Antwortoption angeboten wird.",
    limitations: "Anordnung, Objektgrößen und Bearbeitungsverhalten werden nicht als Ursache interpretiert. Weitergehende Aussagen zur simultanen Mengenerfassung sind daher nicht möglich.",
    variants: [], ruleIds: ["mengen-match.number_off_by_one", "mengen-match.number_off_by_two"],
  },
  "zaehlen-tippen": {
    label: "Zählen und Tippen",
    subject: "math",
    coverage: "partial",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Die Analyse prüft ausschließlich, ob die gewählte Gesamtzahl um eins oder zwei von der dargestellten Anzahl abweicht.",
    diagnosisUnit: "Eine dargestellte Menge mit Sollzahl und gewählter Gesamtzahl.",
    denominator: "Je Abstand zählen nur Items, bei denen eine entsprechende Zahl als Antwortoption angeboten wird.",
    limitations: "Tippfolge, Tippzahl, Fokus und Bearbeitungszeit werden bewusst nicht qualitativ interpretiert. Die Analyse erlaubt deshalb keine Aussage zur Eins-zu-eins-Zuordnung oder Zählorganisation.",
    variants: [], ruleIds: ["zaehlen-tippen.number_off_by_one", "zaehlen-tippen.number_off_by_two"],
  },
  sortierspiel: {
    label: "Sortierspiel",
    subject: "math",
    coverage: "none",
    microAnalysis: false,
    macroAnalysis: false,
    analysisForm: "none",
    theory: "Das Sortierspiel prüft die Zuordnung eines Objekts zu einer Kategorie. Die Kategorien sind frei konfigurierbar und besitzen noch keine gemeinsame Fehlertaxonomie.",
    diagnosisUnit: "Noch nicht festgelegt; möglich wären Objekt, Zielkategorie und gewählte Kategorie.",
    denominator: "Es gibt noch keinen qualitativen Nenner.",
    limitations: "Eine generische Analyse wäre ohne deklarative fachliche Bedeutung der Kategorien nicht belastbar.",
    variants: [], ruleIds: [],
  },
  "schriftlich-rechnen": {
    label: "Schriftliches Rechnen",
    subject: "math",
    coverage: "partial",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Für Addition und Subtraktion untersucht die Analyse Überträge, Entbündelungen und konsistente Folgefehler auf Spaltenebene.",
    diagnosisUnit: "Ein Ergebnis-, Übertrags- oder Entbündelungsfeld in seiner Rechenspalte.",
    denominator: "Je Hypothese zählen nur Spalten, in denen ein Übertrag, eine Entbündelung oder ein daraus ableitbarer Folgeschritt tatsächlich vorkommt.",
    limitations: "Multiplikation und Division werden fachlich bewertet, besitzen aber noch keine qualitative Hypothesenlogik.",
    variants: [
      { label: "Addition", coverage: "complete", explanation: "Mikro- und Makroanalyse für Überträge und Folgefehler vorhanden." },
      { label: "Subtraktion", coverage: "complete", explanation: "Mikro- und Makroanalyse für Entbündelungen und Folgefehler vorhanden." },
      { label: "Multiplikation", coverage: "none", explanation: "Noch keine qualitative Analyse." },
      { label: "Division", coverage: "none", explanation: "Noch keine qualitative Analyse." },
    ],
    ruleIds: ["written_arithmetic.carry_omitted", "written_arithmetic.carry_misplaced", "written_arithmetic.borrow_not_propagated", "written_arithmetic.consistent_followup"],
  },
  blitzlesen: {
    label: "Blitzlesen",
    subject: "german",
    coverage: "none",
    microAnalysis: false,
    macroAnalysis: false,
    analysisForm: "none",
    theory: "Richtig- und Falschbewertungen können im normalen Verlauf erscheinen. Anzeigedauer und Tempo werden bewusst nicht für qualitative Hypothesen genutzt.",
    diagnosisUnit: "Noch nicht festgelegt; möglich wäre ein fachlich klassifiziertes Wort oder Pseudowort.",
    denominator: "Es gibt noch keinen qualitativen Nenner.",
    limitations: "Eine qualitative Analyse darf keine itembezogenen Zeit- oder Aufmerksamkeitsmetriken voraussetzen.",
    variants: [], ruleIds: [],
  },
  silbentrennung: {
    label: "Lesesilben trennen",
    subject: "german",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "word_diagnostics",
    theory: "Die Analyse vergleicht gesetzte und erwartete Trennstellen mit orthographischen Einheiten und typischen VCV- beziehungsweise VCCV-Strukturen.",
    diagnosisUnit: "Ein Wort mit orthographischen Einheiten, erwarteten und gesetzten Trennstellen.",
    denominator: "Je Hypothese zählen nur Wörter, die die erforderliche Struktur enthalten, etwa einen Mehrgrafen oder eine passende Konsonantenbrücke.",
    limitations: "Die Analyse folgt der in Calepia verwendeten Lesesilbenlogik und ist keine allgemeine Duden-Silbentrennung.",
    variants: [],
    ruleIds: ["silbentrennung.no_nucleus", "silbentrennung.locked_unit_split", "silbentrennung.single_consonant_left", "silbentrennung.consonant_cluster_not_split"],
  },
  reime: {
    label: "Reime erkennen",
    subject: "german",
    coverage: "none",
    microAnalysis: false,
    macroAnalysis: false,
    analysisForm: "none",
    theory: "Aktuell wird nur entschieden, ob die Ja-Nein-Antwort zum Wortpaar passt. Arten von Reimähnlichkeit und typische Ablenker werden noch nicht ausgewertet.",
    diagnosisUnit: "Noch nicht festgelegt; möglich wären Wortpaar, Reimstruktur und Distraktortyp.",
    denominator: "Es gibt noch keinen qualitativen Nenner.",
    limitations: "Für Hypothesen müssten Nicht-Reime nach phonologischer Nähe fachlich klassifiziert werden.",
    variants: [], ruleIds: [],
  },
  zeitformen: {
    label: "Zeitformen erkennen",
    subject: "german",
    coverage: "none",
    microAnalysis: false,
    macroAnalysis: false,
    analysisForm: "none",
    theory: "Die gewählte Zeitform wird fachlich geprüft, Verwechslungen zwischen bestimmten Tempuspaaren werden aber noch nicht als Hypothesen aggregiert.",
    diagnosisUnit: "Noch nicht festgelegt; möglich wären Satz, Zieltempus und gewähltes Tempus.",
    denominator: "Es gibt noch keinen qualitativen Nenner.",
    limitations: "Ein Analyse-Contract müsste Satzmerkmale und fachlich relevante Tempuskontraste deklarativ festhalten.",
    variants: [], ruleIds: [],
  },
  "zeitformen-konjugieren": {
    label: "Zeitformen konjugieren",
    subject: "german",
    coverage: "none",
    microAnalysis: false,
    macroAnalysis: false,
    analysisForm: "none",
    theory: "Die sechs Personalformen werden derzeit auf Richtigkeit geprüft. Stamm-, Endungs-, Hilfsverb- und Tempusfehler werden noch nicht getrennt.",
    diagnosisUnit: "Noch nicht festgelegt; möglich wären einzelne Personalform und morphologische Teilstruktur.",
    denominator: "Es gibt noch keinen qualitativen Nenner.",
    limitations: "Vor einer Analyse braucht es eine Fehlertaxonomie für Person, Numerus, Stamm, Hilfsverb und Tempusbildung.",
    variants: [], ruleIds: [],
  },
  "dehnungs-h": {
    label: "Dehnungs-h",
    subject: "german",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "word_diagnostics",
    theory: "Regelwörter mit Dehnungs-h werden mit Kontrastwörtern ohne h verglichen, um allgemeine Unsicherheit von Übergeneralisierung zu unterscheiden.",
    diagnosisUnit: "Ein einzelnes Regel- oder Kontrastwort mit gewählter und erwarteter Schreibweise.",
    denominator: "Nur beantwortete diagnostische Wörter zählen; Kontrasthypothesen benötigen getrennte Mindestmengen für Regel- und Kontrastwörter.",
    limitations: "Die Wortauswahl bildet kuratierte Muster ab. Die Hinweise gelten nicht automatisch für sämtliche Wörter mit langem Vokal.",
    variants: [],
    ruleIds: ["dehnungs-h.rule_uncertainty", "dehnungs-h.overgeneralization"],
  },
  "kurze-lange-vokale": {
    label: "Kurze und lange Vokale",
    subject: "german",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "word_diagnostics",
    theory: "Regelwörter mit notwendiger Konsonantenverdoppelung werden Kontrastwörtern mit langem Vokal gegenübergestellt.",
    diagnosisUnit: "Ein Regel- oder Kontrastwort mit gewählter und erwarteter Schreibweise.",
    denominator: "Nur beantwortete diagnostische Wörter zählen; die Hypothesen benötigen getrennte Mindestmengen für beide Wortrollen.",
    limitations: "Die Analyse beschreibt die Entscheidung im Wortkontrast, nicht unmittelbar die auditive Wahrnehmungsleistung.",
    variants: [],
    ruleIds: ["kurze-lange-vokale.overgeneralization", "kurze-lange-vokale.omission", "kurze-lange-vokale.length_uncertainty"],
  },
  "morphem-stamm": {
    label: "Stamm-Trainer",
    subject: "german",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "word_diagnostics",
    theory: "Graphemwahl und Stammwortbegründung werden gekoppelt ausgewertet. Dadurch lassen sich Treffer ohne tragfähige Ableitung von fachlich begründeten Entscheidungen unterscheiden.",
    diagnosisUnit: "Ein Zielwort mit Graphemwahl, gewähltem Stammwort und dokumentierten Umzuweisungen.",
    denominator: "Es zählen vollständig bearbeitete gekoppelte Entscheidungen; einzelne Regeln besitzen eigene Mindestzahlen.",
    limitations: "Umzuweisungen können produktive Selbstkorrektur oder Unsicherheit bedeuten und müssen mit dem Ergebnis zusammen gelesen werden.",
    variants: [],
    ruleIds: ["morphem-stamm.phonetic_surface_bias", "morphem-stamm.visual_homophone_error", "morphem-stamm.lucky_guess", "morphem-stamm.reflective_reassignment"],
  },
  "morphem-zerlegung": {
    label: "Wortbausteine zerlegen",
    subject: "german",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "word_diagnostics",
    theory: "Die Analyse unterscheidet morphologische Bausteingrenzen von Sprechsilben und prüft Präfix-, Stamm- und Suffixplätze getrennt.",
    diagnosisUnit: "Ein Wort mit erwarteten Morphemgrenzen, gesetzten Schnitten und deklarierter Wortstruktur.",
    denominator: "Je Regel zählen nur Wörter mit der erforderlichen Struktur, etwa vorhandenem Präfix, Suffix oder dokumentierter Silbenalternative.",
    limitations: "Die Aussagekraft hängt von fachlich korrekt gepflegten Morphem- und Kontrastangaben im Aufgabenbestand ab.",
    variants: [],
    ruleIds: ["morphem-zerlegung.syllable_trap", "morphem-zerlegung.prefix_stem_blend", "morphem-zerlegung.suffix_stem_blend", "morphem-zerlegung.prefix_slot_instability", "morphem-zerlegung.suffix_slot_instability"],
  },
  wortarten: {
    label: "Wortarten",
    subject: "german",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "word_diagnostics",
    theory: "Die Analyse verbindet die gewählte Wortart mit syntaktischen Kontrastkontexten und fachlich sichtbaren Umzuweisungen.",
    diagnosisUnit: "Ein Satz-Token mit erwarteter und gewählter Wortart, syntaktischem Kontext und Zuordnungsreihenfolge.",
    denominator: "Je Hypothese zählen nur Tokens oder Sätze mit dem passenden Kontext, etwa Nominalisierung, Satzanfang oder adverbialer Gebrauch.",
    limitations: "Reihenfolge und Umzuweisungen werden nur zusammen mit fachlichen Fehlmustern interpretiert; Zeit- und Fokusmetriken werden nicht genutzt.",
    variants: [],
    ruleIds: ["wortarten.capitalized_distractor", "wortarten.nominalized", "wortarten.adverbial_use", "wortarten.linear_processing", "wortarten.reassignments"],
  },
  grossklein: {
    label: "Groß- und Kleinschreibung",
    subject: "german",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "word_diagnostics",
    theory: "Die Analyse trennt übersehene Basisfälle und Nominalisierungssignale von einer kompensatorischen Übergeneralisierung.",
    diagnosisUnit: "Ein Satz-Token mit Großschreibungserwartung, Markierung, Wortart und syntaktischem Kontext.",
    denominator: "Je Hypothese zählen nur Tokens des passenden Kontextes, beispielsweise Satzanfänge, Standard-Nomen oder Signal-Nominalisierungen.",
    limitations: "Nicht markierte relevante Tokens werden als Auslassung erfasst; neutrale Wörter gehen nicht in fachfremde Nenner ein.",
    variants: [],
    ruleIds: ["grossklein.base_miss", "grossklein.nominalization_signal_blindness", "grossklein.compensatory_overgeneralization"],
  },
  satzglieder: {
    label: "Satzglieder",
    subject: "german",
    coverage: "complete",
    microAnalysis: true,
    macroAnalysis: true,
    analysisForm: "rule_analyzer",
    theory: "Die Analyse betrachtet Satzteile in ihrer strukturellen Rolle und unterscheidet Positions-Bias, Rollenvertauschungen, Kasus- und Ergänzungsfehler sowie Prädikatsklammern.",
    diagnosisUnit: "Ein Satzglied-Chunk mit erwarteter und gewählter Rolle innerhalb einer strukturierten Satzvariante.",
    denominator: "Je Regel zählen nur passende Satzteile, etwa vorangestellte Nicht-Subjekte, Dativobjekte oder hintere Teile einer Prädikatsklammer.",
    limitations: "Gespeichert wird der fachliche Endzustand. Itembezogene Zeit-, Fokus- und Ablenkungswerte werden nicht ausgewertet.",
    variants: [],
    ruleIds: ["satzglieder.linear_bias", "satzglieder.role_inversion", "satzglieder.split_predicate", "satzglieder.dative_overgeneralization", "satzglieder.adverbial_object_confusion", "satzglieder.omission"],
  },
} satisfies Record<ExerciseType, EntryDefinition>;

const sharedMacroClusterIds: Record<string, string> = {
  "rechenmauer.plus_minus_one": "math.result_neighbor_1",
  "mathe.counting_error_1": "math.result_neighbor_1",
  "frosch-hupfen.counting_error_1": "math.result_neighbor_1",
  "mathe.counting_error_2": "math.result_neighbor_2",
  "frosch-hupfen.counting_error_2": "math.result_neighbor_2",
  "rechenmauer.inversion": "math.two_digit_inversion",
  "mathe.two_digit_inversion": "math.two_digit_inversion",
  "mathe.plus_minus_confusion": "math.plus_minus_confusion",
  "frosch-hupfen.plus_minus_confusion": "math.plus_minus_confusion",
  "mathe.place_value_subtraction_inversion": "math.place_value_subtraction_inversion",
  "frosch-hupfen.place_value_subtraction_inversion": "math.place_value_subtraction_inversion",
  "rechenmauer.consistent_followup": "math.consistent_followup",
  "stellenwert_addition.consistent_followup": "math.consistent_followup",
  "written_arithmetic.consistent_followup": "math.consistent_followup",
  "mengen-match.number_off_by_one": "math.quantity_number_off_by_one",
  "zaehlen-tippen.number_off_by_one": "math.quantity_number_off_by_one",
  "mengen-match.number_off_by_two": "math.quantity_number_off_by_two",
  "zaehlen-tippen.number_off_by_two": "math.quantity_number_off_by_two",
};

const macroClusterLabels: Record<string, string> = {
  "math.result_neighbor_1": "Rechenergebnis um eins verfehlt",
  "math.result_neighbor_2": "Rechenergebnis um zwei verfehlt",
  "math.two_digit_inversion": "Zweistelliger Zahlendreher",
  "math.plus_minus_confusion": "Plus und Minus verwechselt",
  "math.place_value_subtraction_inversion": "Zehner-Einer-Fehler beim Subtrahieren",
  "math.consistent_followup": "Konsistentes Weiterrechnen",
  "math.quantity_number_off_by_one": "Menge-Zahl-Zuordnung um eins verfehlt",
  "math.quantity_number_off_by_two": "Menge-Zahl-Zuordnung um zwei verfehlt",
};

const macroClusterExplanations: Record<string, string> = {
  "math.result_neighbor_1": "Ein Rechenergebnis liegt genau um eins über oder unter dem richtigen Wert.",
  "math.result_neighbor_2": "Ein Rechenergebnis liegt genau um zwei über oder unter dem richtigen Wert.",
  "math.two_digit_inversion": "Die beiden Ziffern eines unterscheidbaren zweistelligen Ergebnisses erscheinen in umgekehrter Reihenfolge.",
  "math.plus_minus_confusion": "Die Antwort entspricht bei Addition oder Subtraktion exakt dem Ergebnis der jeweils entgegengesetzten Operation.",
  "math.place_value_subtraction_inversion": "Beim Subtrahieren über den Zehner passt die Antwort zur isolierten oder umgekehrten Rechnung mit den Einerstellen.",
  "math.consistent_followup": "Nach einem tatsächlich entstandenen Ausgangsfehler wird ein anschließender, auswertbarer Schritt mit den eigenen falschen Vorwerten rechnerisch stimmig fortgesetzt.",
  "math.quantity_number_off_by_one": "Die gewählte Zahl liegt genau um eins neben der dargestellten Anzahl.",
  "math.quantity_number_off_by_two": "Die gewählte Zahl liegt genau um zwei neben der dargestellten Anzahl.",
};

function mathMacroDomain(ruleId: string) {
  if (ruleId.startsWith("transkodieren.")) return "math.place_value";
  if (ruleId.includes("counting_error") || ruleId === "rechenmauer.plus_minus_one") return "math.number_sequence";
  if (ruleId.includes("plus_minus_confusion") || ruleId.includes("mult_div_confusion") || ruleId.includes("instead_of")) return "math.operations";
  if (ruleId.includes("consistent_followup")) return "math.part_whole";
  if (ruleId.includes("inversion") || ruleId.includes("place_value") || ruleId.startsWith("stellenwert_addition.")) return "math.place_value";
  if (ruleId.startsWith("mengen-match.") || ruleId.startsWith("zaehlen-tippen.")) return "math.quantity_number";
  if (ruleId.startsWith("runden.")) return "math.rounding";
  if (ruleId.startsWith("written_arithmetic.")) return "math.written_procedures";
  return "math.facts_rules";
}

function macroEvaluationUnit(ruleId: string) {
  if (ruleId.startsWith("laute-erkennen.")) return "item";
  if (ruleId.startsWith("rechenmauer.") || ruleId.startsWith("written_arithmetic.")) return "field";
  if (["transkodieren.", "mathe.", "frosch-hupfen.", "mengen-match.", "zaehlen-tippen.", "runden.", "stellenwert_addition.", "silbentrennung."].some((prefix) => ruleId.startsWith(prefix))) return "item";
  if (ruleId.startsWith("satzglieder.")) return "sentence_chunk";
  if (["dehnungs-h.", "kurze-lange-vokale.", "morphem-stamm.", "morphem-zerlegung.", "wortarten.", "grossklein."].some((prefix) => ruleId.startsWith(prefix))) return "token";
  return "run";
}

function additiveAcrossLogs(ruleId: string) {
  return !["dehnungs-h.", "kurze-lange-vokale.", "wortarten.", "morphem-stamm."].some((prefix) => ruleId.startsWith(prefix));
}

export const qualitativeAnalysisCatalog: readonly QualitativeAnalysisCatalogEntry[] = Object.freeze(
  (Object.entries(entries) as Array<[ExerciseType, EntryDefinition]>).map(([exerciseType, entry]) => ({
    exerciseType,
    ...entry,
    hypotheses: entry.ruleIds.map((ruleId) => ({
      ruleId,
      macroAggregation: true,
      macroClusterId: sharedMacroClusterIds[ruleId] ?? ruleId,
      macroDomainId: entry.subject === "math" ? mathMacroDomain(ruleId) : null,
      macroPolarity: ruleId.endsWith(".consistent_followup") ? "strength" as const : "concern" as const,
      ...hypothesisExplanation(ruleId, ruleId),
    })),
  })),
);

type MutableMacroCluster = Omit<QualitativeMacroCluster, "ruleIds" | "evaluationUnits"> & { ruleIds: string[]; evaluationUnits: string[] };
const macroClusters = new Map<string, MutableMacroCluster>();
for (const entry of qualitativeAnalysisCatalog) {
  for (const hypothesis of entry.hypotheses.filter(({ macroAggregation }) => macroAggregation)) {
    const existing = macroClusters.get(hypothesis.macroClusterId);
    if (existing) {
      existing.ruleIds.push(hypothesis.ruleId);
      if (!existing.evaluationUnits.includes(macroEvaluationUnit(hypothesis.ruleId))) existing.evaluationUnits.push(macroEvaluationUnit(hypothesis.ruleId));
      existing.additiveAcrossLogs = existing.additiveAcrossLogs && additiveAcrossLogs(hypothesis.ruleId);
      continue;
    }
    macroClusters.set(hypothesis.macroClusterId, {
      id: hypothesis.macroClusterId,
      label: macroClusterLabels[hypothesis.macroClusterId] ?? hypothesis.label,
      subject: entry.subject,
      domainId: hypothesis.macroDomainId,
      polarity: hypothesis.macroPolarity,
      explanation: macroClusterExplanations[hypothesis.macroClusterId] ?? hypothesis.statement,
      ruleIds: [hypothesis.ruleId],
      evaluationUnits: [macroEvaluationUnit(hypothesis.ruleId)],
      additiveAcrossLogs: additiveAcrossLogs(hypothesis.ruleId),
    });
  }
}

export const qualitativeMacroClusters: readonly QualitativeMacroCluster[] = Object.freeze([...macroClusters.values()].map((cluster) => Object.freeze({
  ...cluster,
  ruleIds: Object.freeze([...cluster.ruleIds]),
  evaluationUnits: Object.freeze([...cluster.evaluationUnits]),
})));
